import React from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import ClearableInput, {
  SettingsField,
} from '../../../components/Settings/ClearableInput'
import SettingsCard from '../../../components/Settings/SettingsCard'
import { SOCIAL_FIELDS, socialUrlStatus } from '../../../lib/socialLinks'

/**
 * Social profile links.
 *
 * The ✓/✗ indicator reflects URL *format* only — there is no endpoint that
 * verifies a link resolves or belongs to this user, so it must not be labelled
 * "verified".
 */
export default function SocialMediaCard({
  user,
  setUser,
  setHasSocialsChanged,
  onSaveSocials,
  hasSocialsChanged,
  isSavingSocials,
}) {
  const { t } = useTranslation()

  const update = (key, value) => {
    setUser({ ...user, [key]: value })
    setHasSocialsChanged(true)
  }

  const anyInvalid = SOCIAL_FIELDS.some(
    field => socialUrlStatus(user?.[field.key]) === 'invalid',
  )

  return (
    <SettingsCard
      icon="share-nodes"
      title={t('users.edit.socials')}
      actions={
        <>
          <button
            className="btn btn-primary"
            type="button"
            onClick={onSaveSocials}
            disabled={!hasSocialsChanged || isSavingSocials || anyInvalid}
          >
            {isSavingSocials ? t('users.edit.saving') : t('users.edit.save')}
          </button>
          {anyInvalid && (
            <span className="text-danger small">
              {t(
                'users.edit.socials_invalid',
                'Fix the highlighted links before saving.',
              )}
            </span>
          )}
        </>
      }
    >
      <div className="row">
        {SOCIAL_FIELDS.map(field => {
          const value = user?.[field.key] ?? ''
          const status = socialUrlStatus(value)
          return (
            <div className="col-md-6" key={field.key}>
              <SettingsField
                label={t(field.labelKey)}
                htmlFor={field.key}
                status={status}
                help={
                  status === 'invalid'
                    ? t(
                        'users.edit.social_invalid_url',
                        'Enter a full URL, e.g. https://example.com/you',
                      )
                    : null
                }
                helpVariant={status === 'invalid' ? 'error' : undefined}
              >
                <ClearableInput
                  id={field.key}
                  name={field.key}
                  type="url"
                  placeholder={field.placeholder}
                  value={value}
                  onChange={newValue => update(field.key, newValue)}
                  onClear={() => update(field.key, '')}
                  clearLabel={t('settings.field.clear', 'Clear')}
                />
              </SettingsField>
            </div>
          )
        })}
      </div>
    </SettingsCard>
  )
}

SocialMediaCard.propTypes = {
  user: PropTypes.object,
  setUser: PropTypes.func.isRequired,
  setHasSocialsChanged: PropTypes.func.isRequired,
  onSaveSocials: PropTypes.func.isRequired,
  hasSocialsChanged: PropTypes.bool,
  isSavingSocials: PropTypes.bool,
}
